// src/domain/elements/Element.ts

/**
 * Shape of the base element properties as they come from queries / plain objects.
 * This is what your query helpers should return and what subclasses (Electric,
 * Lighting, etc.) will pass into `super(...)`.
 */
export type ElementProps = {
  /** Model identifier */
  Model: string;

  /** Last modified date and time */
  LastMod: Date;

  /** Code specification */
  CodeSpec: string;

  /** Code scope */
  CodeScope: string;

  /** Code value */
  CodeValue: string;

  /** User label */
  UserLabel: string;

  /** Parent identifier */
  Parent: string;

  /** Federation GUID */
  FederationGUID: string;

  /** JSON properties string */
  JSONProperties: string;
};

/**
 * Element - Abstract base class for all element types in the system.
 * Represents the top-level element structure with common properties.
 *
 * NOTE: Property names match the external data source schema.
 */
export abstract class Element {
  Model: string;
  LastMod: Date;
  CodeSpec: string;
  CodeScope: string;
  CodeValue: string;
  UserLabel: string;
  Parent: string;
  FederationGUID: string;
  JSONProperties: string;

  /**
   * Base constructor – called from subclasses (Electric, Lighting, etc.)
   */
  protected constructor(props: ElementProps) {
    this.Model = props.Model;
    this.LastMod = props.LastMod;
    this.CodeSpec = props.CodeSpec;
    this.CodeScope = props.CodeScope;
    this.CodeValue = props.CodeValue;
    this.UserLabel = props.UserLabel;
    this.Parent = props.Parent;
    this.FederationGUID = props.FederationGUID;
    this.JSONProperties = props.JSONProperties;
  }

  /**
   * Small helper you can reuse in subclasses' `fromObject` methods.
   * Takes a loose object (e.g. from ECSQL / JSON) and normalizes it into
   * a fully-populated `ElementProps`.
   */
  static normalizeProps(obj: Partial<ElementProps>): ElementProps {
    // LastMod normalization
    let lastMod: Date;
    if (obj.LastMod instanceof Date) {
      lastMod = obj.LastMod;
    } else if (typeof obj.LastMod === "string") {
      lastMod = new Date(obj.LastMod);
    } else {
      lastMod = new Date();
    }

    return {
      Model: obj.Model ?? "",
      LastMod: lastMod,
      CodeSpec: obj.CodeSpec ?? "",
      CodeScope: obj.CodeScope ?? "",
      CodeValue: obj.CodeValue ?? "",
      UserLabel: obj.UserLabel ?? "",
      Parent: obj.Parent ?? "",
      FederationGUID: obj.FederationGUID ?? "",
      JSONProperties: obj.JSONProperties ?? "",
    };
  }

  /**
   * Base `fromObject` – left intentionally unusable so you don't accidentally
   * try to construct a raw Element. Each concrete subclass should implement
   * its own `fromObject` that *delegates* to `Element.normalizeProps`.
   *
   * Example in a subclass:
   *   static fromObject(obj: Partial<MySubclass>): MySubclass {
   *     const base = Element.normalizeProps(obj);
   *     return new MySubclass({ ...base, ...otherProps });
   *   }
   */
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  static fromObject(_obj: Partial<Element>): Element {
    throw new Error("fromObject must be implemented by concrete Element subclass");
  }

  /**
   * Converts the Element to a plain object (useful for serialization)
   */
  toObject(): Record<string, unknown> {
    return {
      Model: this.Model,
      LastMod: this.LastMod.toISOString(),
      CodeSpec: this.CodeSpec,
      CodeScope: this.CodeScope,
      CodeValue: this.CodeValue,
      UserLabel: this.UserLabel,
      Parent: this.Parent,
      FederationGUID: this.FederationGUID,
      JSONProperties: this.JSONProperties,
    };
  }
}

